import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Progress } from '../components/ui/progress';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { toast } from 'sonner';
import FramedAvatar from '../components/FramedAvatar';
import {
  ArrowLeft, Trophy, MessageSquare, Heart, Target, Star,
  TrendingUp, Calendar, Award, Zap, Clock, BarChart3,
  Compass, Anchor, Flame
} from 'lucide-react';

const MyStatsPage = () => {
  const { user, api } = useAuth();
  const navigate = useNavigate();
  
  const [stats, setStats] = useState(null);
  const [badges, setBadges] = useState([]);
  const [activity, setActivity] = useState([]);
  const [activityHistory, setActivityHistory] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchAllData();
  }, []);

  const fetchAllData = async () => {
    try {
      const [statsRes, badgesRes, victoriesRes, ideasRes, historyRes] = await Promise.all([
        api.get('/users/me/stats'),
        api.get(`/badges/user/${user.id}`),
        api.get('/victories?limit=50'),
        api.get('/ideas'),
        api.get('/users/me/activity-history').catch(() => ({ data: null }))
      ]);
      
      setStats(statsRes.data);
      setBadges(badgesRes.data);
      setActivityHistory(historyRes.data);
      
      const myVictories = victoriesRes.data.filter(v => v.user_id === user.id);
      const myCongrats = victoriesRes.data.filter(v => v.congratulations?.includes(user.id));
      const myIdeas = ideasRes.data.filter(i => i.user_id === user.id);
      const myVotes = ideasRes.data.filter(i => i.votes?.includes(user.id));
      
      const activityItems = [
        ...myVictories.map(v => ({ type: 'victory', date: v.created_at, title: v.title })),
        ...myCongrats.map(v => ({ type: 'congrats', date: v.created_at, title: `Encouragement: ${v.title}` })),
        ...myIdeas.map(i => ({ type: 'idea', date: i.created_at, title: i.title })),
        ...myVotes.map(i => ({ type: 'vote', date: i.created_at, title: `Vote: ${i.title}` }))
      ].sort((a, b) => new Date(b.date) - new Date(a.date));
      
      setActivity(activityItems.slice(0, 20));
    } catch (error) {
      setStats({
        messages_count: user.messages_count || 0, victories_count: 0,
        victories_supported: user.victories_supported || 0, ideas_proposed: 0,
        votes_cast: 0, points: user.points || 0, level: user.level || 1,
        chapters_completed: user.modules_completed?.length || 0, total_chapters: 15,
        member_since: user.created_at, days_active: 0
      });
    } finally {
      setLoading(false);
    }
  };

  const earnedBadges = badges.filter(b => b.earned);
  const unearnedBadges = badges.filter(b => !b.earned);
  const getInitials = (fn, ln) => `${fn?.[0] || ''}${ln?.[0] || ''}`.toUpperCase();

  const getActivityIcon = (type) => {
    switch (type) {
      case 'victory': return <Trophy className="w-4 h-4 text-yellow-500" />;
      case 'congrats': return <Heart className="w-4 h-4 text-red-500" />;
      case 'idea': return <Zap className="w-4 h-4 text-purple-500" />;
      case 'vote': return <Star className="w-4 h-4 text-orange-500" />;
      default: return <Clock className="w-4 h-4 text-muted-foreground" />;
    }
  };

  // Activity chart - simple bar chart
  const ActivityChart = ({ data }) => {
    if (!data || data.length === 0) return null;
    const max = Math.max(...data.map(d => d.messages), 1);
    return (
      <div className="flex items-end gap-1 h-32" data-testid="activity-chart">
        {data.map((d, i) => (
          <div key={i} className="flex-1 flex flex-col items-center gap-1">
            <div
              className="w-full bg-primary/80 rounded-t transition-all hover:bg-primary"
              style={{ height: `${Math.max(4, (d.messages / max) * 100)}%`, minHeight: d.messages > 0 ? '8px' : '4px' }}
              title={`${d.week}: ${d.messages} messages`}
            />
            {i % 2 === 0 && <span className="text-[9px] text-muted-foreground">{d.week}</span>}
          </div>
        ))}
      </div>
    );
  };

  // Streak calendar - last 90 days
  const StreakCalendar = ({ loginDates }) => {
    const days = [];
    const today = new Date();
    const dateSet = new Set(loginDates || []);
    
    for (let i = 89; i >= 0; i--) {
      const d = new Date(today);
      d.setDate(d.getDate() - i);
      const dateStr = d.toISOString().split('T')[0];
      days.push({ date: dateStr, active: dateSet.has(dateStr), day: d.getDate() });
    }
    
    return (
      <div className="grid grid-cols-15 gap-[3px]" data-testid="streak-calendar" style={{ gridTemplateColumns: 'repeat(15, 1fr)' }}>
        {days.map((d, i) => (
          <div
            key={i}
            className={`aspect-square rounded-sm transition-colors ${
              d.active 
                ? 'bg-green-500 hover:bg-green-400' 
                : 'bg-secondary/50 hover:bg-secondary'
            }`}
            title={`${d.date}${d.active ? ' - Connecte' : ''}`}
          />
        ))}
      </div>
    );
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="text-center">
          <BarChart3 className="w-12 h-12 text-primary animate-pulse mx-auto mb-4" />
          <p className="text-muted-foreground">Chargement de tes statistiques...</p>
        </div>
      </div>
    );
  }

  const statCards = [
    { icon: <MessageSquare className="w-5 h-5" />, label: 'Messages', value: stats?.messages_count || user.messages_count || 0, color: 'text-blue-500' },
    { icon: <Trophy className="w-5 h-5" />, label: 'Victoires', value: stats?.victories_count || 0, color: 'text-yellow-500' },
    { icon: <Heart className="w-5 h-5" />, label: 'Encouragements', value: stats?.victories_supported || 0, color: 'text-red-500' },
    { icon: <Zap className="w-5 h-5" />, label: 'Idees', value: stats?.ideas_proposed || 0, color: 'text-purple-500' },
    { icon: <Star className="w-5 h-5" />, label: 'Votes', value: stats?.votes_cast || 0, color: 'text-orange-500' },
    { icon: <Award className="w-5 h-5" />, label: 'Badges', value: earnedBadges.length, color: 'text-green-500' },
  ];

  return (
    <div className="min-h-screen bg-background" data-testid="my-stats-page">
      <div className="noise-overlay" />

      <header className="border-b border-border bg-card/50 backdrop-blur-xl sticky top-0 z-10">
        <div className="max-w-5xl mx-auto px-4 py-4 flex items-center gap-4">
          <Button variant="ghost" size="icon" onClick={() => navigate('/')}>
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-primary/20 flex items-center justify-center">
              <BarChart3 className="w-5 h-5 text-primary" />
            </div>
            <div>
              <h1 className="font-heading font-bold text-xl">Mes Statistiques</h1>
              <p className="text-sm text-muted-foreground">Ton parcours dans la communaute</p>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-4 py-8 space-y-8">
        {/* Profile Header with FramedAvatar */}
        <Card className="card-base overflow-hidden relative">
          <div className="absolute inset-0 bg-gradient-to-br from-primary/10 via-transparent to-blue-500/10" />
          <CardContent className="p-6 relative">
            <div className="flex flex-col md:flex-row items-center gap-6">
              <FramedAvatar
                src={user.avatar_url}
                fallback={getInitials(user.first_name, user.last_name)}
                frame={user.profile_frame}
                role={user.role}
                seniority={user.seniority}
                size="xl"
              />
              
              <div className="flex-1 text-center md:text-left">
                <h2 className="font-heading font-bold text-2xl">{user.first_name} {user.last_name}</h2>
                <div className="flex flex-wrap items-center justify-center md:justify-start gap-2 mt-2">
                  <Badge className="bg-primary/20 text-primary">{user.role}</Badge>
                  <Badge variant="outline">
                    <Anchor className="w-3 h-3 mr-1" />Niveau {stats?.level || user.level || 1}
                  </Badge>
                  <Badge variant="outline">
                    <Star className="w-3 h-3 mr-1" />{stats?.points || user.points || 0} XP
                  </Badge>
                  {activityHistory && (
                    <Badge variant="outline" className="text-orange-500 border-orange-500/30">
                      <Flame className="w-3 h-3 mr-1" />Streak: {activityHistory.current_streak}j
                    </Badge>
                  )}
                </div>
                
                <div className="mt-3 text-sm text-muted-foreground flex flex-wrap gap-4 justify-center md:justify-start">
                  <span><Calendar className="w-3 h-3 inline mr-1" />Membre depuis {stats?.days_active || 0} jours</span>
                  <span><Compass className="w-3 h-3 inline mr-1" />{stats?.chapters_completed || 0}/{stats?.total_chapters || 15} chapitres</span>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Stats Grid */}
        <div className="grid grid-cols-3 md:grid-cols-6 gap-3">
          {statCards.map((stat, index) => (
            <Card key={index} className="card-base text-center" data-testid={`stat-card-${index}`}>
              <CardContent className="p-3">
                <span className={stat.color}>{stat.icon}</span>
                <div className="text-xl font-bold mt-1">{stat.value}</div>
                <div className="text-[10px] text-muted-foreground">{stat.label}</div>
              </CardContent>
            </Card>
          ))}
        </div>

        {/* Activity Chart + Streak */}
        <div className="grid md:grid-cols-2 gap-6">
          {/* Weekly Activity */}
          <Card className="card-base">
            <CardHeader className="pb-2">
              <CardTitle className="font-heading text-base flex items-center gap-2">
                <TrendingUp className="w-4 h-4 text-primary" />
                Activite (12 semaines)
              </CardTitle>
            </CardHeader>
            <CardContent>
              {activityHistory?.weekly_messages ? (
                <ActivityChart data={activityHistory.weekly_messages} />
              ) : (
                <p className="text-sm text-muted-foreground text-center py-8">Pas encore de donnees</p>
              )}
            </CardContent>
          </Card>

          {/* Streak Calendar */}
          <Card className="card-base">
            <CardHeader className="pb-2">
              <CardTitle className="font-heading text-base flex items-center gap-2">
                <Flame className="w-4 h-4 text-orange-500" />
                Calendrier de connexion (90 jours)
              </CardTitle>
              <CardDescription className="text-xs">
                {activityHistory?.current_streak || 0} jours consecutifs | Record: {activityHistory?.longest_streak || 0} jours
              </CardDescription>
            </CardHeader>
            <CardContent>
              <StreakCalendar loginDates={activityHistory?.login_dates} />
              <div className="flex items-center gap-3 mt-3 text-xs text-muted-foreground">
                <div className="flex items-center gap-1">
                  <div className="w-3 h-3 rounded-sm bg-green-500" /> Connecte
                </div>
                <div className="flex items-center gap-1">
                  <div className="w-3 h-3 rounded-sm bg-secondary/50" /> Absent
                </div>
                <span className="ml-auto">{activityHistory?.total_login_days || 0} jours au total</span>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Tabs: Badges & Activity */}
        <Tabs defaultValue="badges" className="w-full">
          <TabsList className="bg-secondary/50 w-full justify-start">
            <TabsTrigger value="badges" className="flex items-center gap-2">
              <Award className="w-4 h-4" />Badges ({earnedBadges.length}/{badges.length})
            </TabsTrigger>
            <TabsTrigger value="activity" className="flex items-center gap-2">
              <Clock className="w-4 h-4" />Activite recente
            </TabsTrigger>
          </TabsList>

          <TabsContent value="badges" className="mt-6">
            <div className="space-y-6">
              <div>
                <h3 className="font-heading text-lg mb-4 flex items-center gap-2">
                  <Trophy className="w-5 h-5 text-yellow-500" />Badges obtenus ({earnedBadges.length})
                </h3>
                {earnedBadges.length === 0 ? (
                  <Card className="card-base text-center py-8">
                    <CardContent>
                      <Award className="w-12 h-12 text-muted-foreground mx-auto mb-3" />
                      <p className="text-muted-foreground">Aucun badge obtenu pour l'instant</p>
                    </CardContent>
                  </Card>
                ) : (
                  <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                    {earnedBadges.map((badge) => (
                      <Card key={badge.id} className="card-base border-primary/30 bg-primary/5" data-testid={`badge-${badge.id}`}>
                        <CardContent className="p-4 text-center">
                          <div className="text-4xl mb-2">{badge.icon}</div>
                          <h4 className="font-semibold text-sm">{badge.name}</h4>
                          <p className="text-xs text-muted-foreground mt-1">{badge.description}</p>
                          <Badge variant="outline" className="mt-2 text-xs">{badge.category}</Badge>
                        </CardContent>
                      </Card>
                    ))}
                  </div>
                )}
              </div>
              <div>
                <h3 className="font-heading text-lg mb-4 flex items-center gap-2">
                  <Target className="w-5 h-5 text-muted-foreground" />A debloquer ({unearnedBadges.length})
                </h3>
                <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                  {unearnedBadges.map((badge) => (
                    <Card key={badge.id} className="card-base opacity-50" data-testid={`badge-locked-${badge.id}`}>
                      <CardContent className="p-4 text-center">
                        <div className="text-4xl mb-2 grayscale">{badge.icon}</div>
                        <h4 className="font-semibold text-sm">{badge.name}</h4>
                        <p className="text-xs text-muted-foreground mt-1">{badge.description}</p>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              </div>
            </div>
          </TabsContent>

          <TabsContent value="activity" className="mt-6">
            <Card className="card-base">
              <CardHeader>
                <CardTitle className="font-heading flex items-center gap-2">
                  <Clock className="w-5 h-5 text-primary" />Activite recente
                </CardTitle>
              </CardHeader>
              <CardContent>
                {activity.length === 0 ? (
                  <div className="text-center py-8 text-muted-foreground">
                    <Clock className="w-12 h-12 mx-auto mb-3 opacity-30" />
                    <p>Aucune activite recente</p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {activity.map((item, index) => (
                      <div key={`${item.type}-${item.date}-${index}`} className="flex items-start gap-3 p-3 bg-secondary/20 rounded-lg">
                        <div className="w-8 h-8 rounded-full bg-secondary flex items-center justify-center flex-shrink-0">
                          {getActivityIcon(item.type)}
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="font-medium text-sm truncate">{item.title}</p>
                          <p className="text-xs text-muted-foreground">
                            {new Date(item.date).toLocaleDateString('fr-FR', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' })}
                          </p>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </main>
    </div>
  );
};

export default MyStatsPage;
